<script setup>
import { Field } from 'vee-validate';
import { provide, ref, watch } from 'vue';
import debounce from 'lodash/debounce';
import {
    Combobox,
    ComboboxInput,
    ComboboxOptions,
    ComboboxOption,
} from '@headlessui/vue';
import { requestSearchedRoamingPlans, __, onlyHebrew } from '@/utils';
import Loader from '@components/loader.vue';

const emit = defineEmits(['searchedPlans'])

const mcc = ref('');
const usage_type = ref('data');
const location_type = ref('country');

const searchedPlans = ref('');

const countries = ref(window?.$$countryCodes ? window?.$$countryCodes : []);
const selectedCountry = ref(null);
const filteredCountries = ref([]);
const name = ref('country-combobox');
const searchLoading = ref(false);
const preventDoubleLoad = ref(false);

const fetchSearchedRoamingPlans = async () => {
    searchLoading.value = true;
    try {
        const response = await requestSearchedRoamingPlans(mcc.value, usage_type.value, location_type.value);
        searchedPlans.value = JSON.parse(response.data);
        searchLoading.value = false;
        return searchedPlans?.value?.response;
    } catch (error) {
        console.error('Error fetching plans:', error);
    }
};

const fetchGradientLinkPlans = async (mccValue) => {
    const currentMcc = mccValue || mcc.value;
    const response = await requestSearchedRoamingPlans(currentMcc, usage_type.value, location_type.value);
    searchedPlans.value = JSON.parse(response.data);
    return searchedPlans?.value?.response;
}

const setPreventDoubleLoad = (value) => {
    preventDoubleLoad.value = value;
};

const handleChange = (event) => {
  selectedCountry.value = event.target.value.trim();
  filterCities();
};

const setSelectedCountry = (value) => {
    selectedCountry.value = value;
}

const filterCities = () => {
    if(!selectedCountry.value) {
        filteredCountries.value = [];
        return;
    }
    const searchValue = selectedCountry.value.toLowerCase();
    filteredCountries.value = countries.value.filter(
        (country) =>
        country.countryName.toLowerCase().includes(searchValue) ||
        country.countryNameHeb.toLowerCase().includes(searchValue)
    );
};


const radioUsageType = [
	{ id: 'data', text: __('Data only')},
    { id: 'datacall', text: __('Surfing and talking')},
    { id: 'callsms', text: __('Talking and texting')},
];

const radioLocationType = [
	{ id: 'country', text: __('Local packages')},
    { id: 'region', text: __('Global packages')},
];

const handleRadioInput = (type , value) => {
    if(type === 'usage'){
        usage_type.value = value;
    }

    if(type === 'location'){
        location_type.value = value;
    }
    
    fetchSearchedRoamingPlans();
}

const fetchDebouncedRoamingPlans = debounce(() => {
    fetchSearchedRoamingPlans();
}, 500);

watch(selectedCountry, (value) => {
    if(value?.countryMcc){
        mcc.value = value?.countryMcc;
    }else{
        mcc.value = '';
    }

    if(!preventDoubleLoad.value) {
        fetchDebouncedRoamingPlans();
    }
});

watch(searchedPlans, (plans) => {
    emit('searchedPlans', plans);
});

provide('search', {
    radioLocationType,
    location_type,
    mcc,
    usage_type,
    countries,
    setSelectedCountry,
    fetchGradientLinkPlans,
    setPreventDoubleLoad
})
</script>
<template>
    <div>
        <div class="search-package">
            <div class="search-package__head">
                <div class="search-package__head-input">
                    <Combobox v-model="selectedCountry">
                        <div class="combobox-wrapper">
                            <ComboboxInput
                                @change="handleChange"
                                :displayValue="(item) => item && item?.countryName && item?.countryNameHeb ? `${item.countryName} (${item.countryNameHeb})`: ''"
                                :id="name"
                                :name="name"
                                placeholder="לאן טסים?"
                            />
                            <label :for="name">
                                <i>
                                    <img :src="`${theme_path.url}/resources/images/svg/location.svg`" alt="Search Location Icon" />
                                </i>
                            </label>
                            <Loader class="align-end combobox-loader" color="#000000" v-if="searchLoading" />
                        </div>
                        <ComboboxOptions class="combobox-options">
                            <ComboboxOption
                                class="combobox-option combobox-option--smaller"
                                v-for="country in filteredCountries"
                                :key="country.countryMcc + country.countryName"
                                :value="country"
                            >
                                {{ country.countryNameHeb }} ({{ country.countryName }})
                            </ComboboxOption>
                        </ComboboxOptions>
                    </Combobox>
                </div>
                <button type="button" class="btn btn--arrows">{{ __('Find me a package') }}</button>
            </div><!-- /.search-package__head -->

            <div class="search-package__body">
                <form class="search-package__body-form" action="">

                    <div class="search-package__radio" v-for="radio in radioUsageType" :key="radio.id">
                        <Field
                            hidden
                            type="radio"
                            name="usage_type"
                            :id="radio.id"
                            :value="radio.id"
                            v-model="usage_type"
                            @change="handleRadioInput('usage', radio.id)"
                        />
                        <label :for="radio.id">
                            <strong class="gradient">
                                {{ radio.text }}
                            </strong>
                        </label>
                    </div>

                </form>

                <form class="package-variant-form" action="">
                    <div class="search-package__radio" v-for="radio in radioLocationType" :key="radio.id">
                        <Field
                            hidden
                            type="radio"
                            name="location_type"
                            :id="radio.id"
                            :value="radio.id"
                            v-model="location_type"
                            @change="handleRadioInput('location', radio.id)"
                        />
                        <label :for="radio.id">
                            {{ radio.text }}
                        </label>
                    </div>
                </form>
            </div><!-- /.search-package__body -->
        </div><!-- /.search-package -->
        
        <slot />
    </div>
</template>